import { SalesResponse } from "@/pages/[others]/pages/sales/models/sale.interface";
import { toast } from "sonner";

/** Máximo de intentos automáticos por venta offline (store o PouchDB). */
export const MAX_OFFLINE_SYNC_ATTEMPTS = 5;

/** Venta guardada con otra caja distinta a la abierta en este equipo. */
export const OFFLINE_SALE_STALE_CASHIER_MSG =
  "Esta venta offline pertenece a otra caja. Ábrala de nuevo en este equipo o reenvíela manualmente desde el panel de sincronización.";

/** El servidor rechazó la venta porque la caja de origen ya cerró. */
export const OFFLINE_SALE_CAJA_CERRADA_MSG =
  "La caja de esta venta offline ya cerró. Reenvíela manualmente desde el panel o descártela si ya no aplica.";

const notifiedExhaustedUuids = new Set<string>();

export function isStoreSaleSyncExhausted(sale: SalesResponse): boolean {
  if (sale.sync_auto_retry_exhausted) return true;
  return (sale.sync_attempts ?? 0) >= MAX_OFFLINE_SYNC_ATTEMPTS;
}

export function extractSyncErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: { message?: string; error?: string };
      };
    };
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (status) return `Error del servidor (HTTP ${status})`;
  }

  if (error instanceof Error) return error.message;
  return "Error desconocido";
}

export type StoreSyncBatchResult = {
  success: number;
  errors: number;
  aborted: boolean;
  skippedExhausted: number;
  newlyExhausted: number;
};

/**
 * Aviso cuando una o más ventas agotaron los reintentos automáticos (una vez por venta).
 */
export function notifyStoreSalesNewlyExhausted(
  offlineUuids: string[],
  options?: { force?: boolean }
): void {
  const fresh = offlineUuids.filter(
    (uuid) => options?.force || !notifiedExhaustedUuids.has(uuid)
  );
  if (fresh.length === 0) return;

  fresh.forEach((uuid) => notifiedExhaustedUuids.add(uuid));

  const countLabel = fresh.length === 1 ? "1 venta" : `${fresh.length} ventas`;

  toast.error(
    `No se pudo sincronizar ${countLabel} offline tras ${MAX_OFFLINE_SYNC_ATTEMPTS} intentos. Corrija el error envíela manualmente desde el panel de sincronización.`,
    { duration: 8000 }
  );
}

/**
 * Resumen al terminar un lote con éxitos y fallos (no agotados).
 */
export function notifyPartialBatchSync(result: {
  success: number;
  errors: number;
  skippedExhausted: number;
}): void {
  if (result.success === 0 || result.errors === 0) return;

  toast.warning(
    `Sincronización parcial: ${result.success} venta(s) enviada(s), ${result.errors} con error. Se seguirán reintentando hasta ${MAX_OFFLINE_SYNC_ATTEMPTS} intentos por venta.`,
    { duration: 6000 }
  );
}
